import React from 'react';

import ResultCard from './ResultCard';
import ResultHeader from './ResultHeader';
import ResultTable from './ResultTable';
import DORButtonDropdown from './dors/DORButtonDropdown';
import { formatDate } from './dateHelper';
import { useClaimsShopSettings } from '../ClaimsShopSettingsContext';
import Text from '../../common/Text';
import PlainButton from '../../common/PlainButton/PlainButton';
import Locale from '../../Translate/Locale';
import ClaimsIcon from '../../../icons/claim.svg';
import { ClaimStatus } from '../claims/types';
import { ClaimableShipment } from '../../../api/types';

import styles from './ShipmentCard.scss';

type ShipmentCardProps = ClaimableShipment & {
  reloadSearch?: () => void;
  onClaim: () => void;
  hasShipmentDetailsAccess: boolean;
};

function ShipmentCard(props: ShipmentCardProps) {
  const {
    id,
    creationDate,
    trackingCode,
    customer,
    carrier,
    lastEvent,
    onClaim,
    claimable,
    claimId,
    consumerActionStatus,
    claimStatus,
    claimType,
    claimSubmittedAt,
    hasShipmentDetailsAccess,
    availableDORLanguages = [],
    reloadSearch,
    claimRequirements,
    claimSubmissionInfo,
  } = props;

  const { isEasyDOREnabled, isDORSigningLinkEnabled } = useClaimsShopSettings();
  const isSomeDOROptionEnabled = isDORSigningLinkEnabled || isEasyDOREnabled;

  const showDORButton =
    availableDORLanguages.length > 0 &&
    isSomeDOROptionEnabled &&
    claimRequirements?.declarationOfRecipient === 'required';

  const orderUrl = `/parcel-finder/shipments/${id}`;
  const showClaimButton = claimable && !claimId;
  const showClaimedTag = !!claimId;
  const showDeadline = claimStatus === ClaimStatus.DRAFT;
  const lastSubmissionDate =
    (claimSubmissionInfo && claimType && claimSubmissionInfo[claimType]?.deadline?.lastSubmissionDate) || undefined;

  return (
    <ResultCard>
      <ResultHeader
        name="Shipment"
        labelForId="Shipment tracking code"
        id={trackingCode}
        lastEvent={lastEvent}
        link={orderUrl}
        claimStatus={claimStatus}
        claimType={claimType}
        claimSubmittedAt={claimSubmittedAt}
        consumerActionStatus={consumerActionStatus}
        lastSubmissionDate={lastSubmissionDate}
        showClaimedTag={showClaimedTag}
        showDeadline={showDeadline}
        showDetailsButton={hasShipmentDetailsAccess}
      >
        {showDORButton && (
          <DORButtonDropdown
            shipmentId={id}
            recipientEmail={customer?.email || ''}
            trackingCode={trackingCode}
            availableDORLanguages={availableDORLanguages}
            isEasyDOREnabled={isEasyDOREnabled}
            isDORSigningLinkEnabled={isDORSigningLinkEnabled}
            reloadSearch={reloadSearch}
          />
        )}
        {showClaimButton && (
          <PlainButton icon={ClaimsIcon} onClick={onClaim}>
            Submit a claim
          </PlainButton>
        )}
      </ResultHeader>
      <ResultTable>
        <thead className={styles.tableHeader}>
          <tr>
            <th>
              <Text>
                <Locale>Creation date</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Name</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Address</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Email</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Carrier</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Carrier country</Locale>
              </Text>
            </th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          <tr>
            <td>
              <Text ink>{formatDate(creationDate)}</Text>
            </td>
            <td>
              <Text ink>{customer?.name}</Text>
            </td>
            <td>
              <Text ink>{customer?.address}</Text>
            </td>
            <td>
              <Text ink>{customer?.email}</Text>
            </td>
            <td>
              <Text ink>{carrier?.name}</Text>
            </td>
            <td>
              <Text ink>{carrier?.countryName}</Text>
            </td>
          </tr>
        </tbody>
      </ResultTable>
    </ResultCard>
  );
}

export default React.memo(ShipmentCard);
